import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem } from "../client";
import { toApiError } from "../errors";
import { qk } from "../keys";
import type { Schemas } from "../types";

/**
 * The saved project canvas: `GET/PUT /v1/projects/{project}/canvas`. The layout is the same shape as
 * the `canvas:` block of guv.yaml. Nodes are keyed KIND.NAME (`services.web`, `cron.nightly-report`,
 * `databases.main`, `volumes.uploads`), so a layout survives redeploys and works in every environment.
 */
export type CanvasLayout = Schemas["CanvasLayout"];
export type CanvasGroup = Schemas["CanvasGroup"];
export type CanvasNodePosition = Schemas["CanvasNodePosition"];
export type ProjectCanvas = Schemas["ProjectCanvas"];
export type CanvasDirection = NonNullable<CanvasLayout["direction"]>;

/** A layout with every optional part filled in, which is what the screen works with. */
export type CanvasDoc = Required<Pick<CanvasLayout, "layout" | "direction" | "nodes" | "groups">> & Pick<CanvasLayout, "viewport">;

export interface CanvasState {
  canvas: CanvasDoc;
  /** Drift, warnings and who saved it. Null when the API has no canvas endpoint. */
  meta: ProjectCanvas | null;
  /** ETag to send back as If-Match. */
  etag: string | null;
  /** False when the API has no canvas endpoint yet (older guvd): the screen falls back to graph positions. */
  supported: boolean;
}

export const DEFAULT_CANVAS: CanvasDoc = { layout: "auto", direction: "LR", nodes: {}, groups: [] };

export function normaliseCanvas(c: CanvasLayout | null | undefined): CanvasDoc {
  return {
    layout: c?.layout === "manual" ? "manual" : "auto",
    direction: c?.direction === "TB" ? "TB" : "LR",
    nodes: c?.nodes ?? {},
    groups: (c?.groups ?? []).filter((g) => g.name),
    ...(c?.viewport ? { viewport: c.viewport } : {}),
  };
}

export function canvasQuery(project: string) {
  return {
    queryKey: qk.canvas(project),
    queryFn: async (): Promise<CanvasState> => {
      let res: Awaited<ReturnType<typeof getCanvas>>;
      try {
        res = await getCanvas(project);
      } catch (e) {
        throw toApiError(e);
      }
      // The project route has already checked the project exists, so a 404 here means an API without the endpoint.
      if ([404, 405, 501].includes(res.response.status)) return { canvas: DEFAULT_CANVAS, meta: null, etag: null, supported: false };
      if (res.error !== undefined || !res.data) throw toApiError(res.error, res.response);
      return { canvas: normaliseCanvas(res.data.canvas), meta: res.data, etag: res.response.headers.get("ETag") ?? res.data.etag, supported: true };
    },
    staleTime: 60_000,
    enabled: !!project,
  };
}

const getCanvas = (project: string) => api.GET("/v1/projects/{project}/canvas", { params: { path: { project } } });

export function useProjectCanvas(project: string) {
  return useQuery(canvasQuery(project));
}

/**
 * Save the whole layout (PUT replaces it) with If-Match. A 412 comes back as an ApiError for the
 * caller to handle; on success the cache takes the server's answer and its new ETag.
 */
export function useSaveProjectCanvas(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, etag }: { body: CanvasLayout; etag: string | null }): Promise<CanvasState> => {
      const res = await api
        .PUT("/v1/projects/{project}/canvas", {
          params: { path: { project }, header: { ...idem(), ...(etag ? { "If-Match": etag } : {}) } },
          body,
        })
        .catch((e: unknown) => {
          throw toApiError(e);
        });
      if (res.error !== undefined || !res.response.ok) throw toApiError(res.error, res.response);
      const data = res.data as ProjectCanvas;
      return { canvas: normaliseCanvas(data.canvas ?? body), meta: data, etag: res.response.headers.get("ETag") ?? data.etag ?? null, supported: true };
    },
    onSuccess: (state) => qc.setQueryData(qk.canvas(project), state),
  });
}
